import { useState, type ComponentType, type ReactNode } from 'react';
import { MoreHorizontalIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export interface SheetAction {
  label: string;
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

/**
 * A row's actions on a phone (spec 263): one ⋯ that opens a sheet of them,
 * named after the row.
 *
 * Where a desktop row shows two or three icon buttons, a phone card has room
 * for none of them at a size a thumb can hit. The sheet rises from the bottom
 * like every dialog on a phone (spec 261), and each action is a full-width row.
 */
export function ActionSheet({
  name,
  description,
  actions,
}: {
  /** What the actions act on; the ⋯ is "Actions for {name}". */
  name: string;
  description?: ReactNode;
  actions: readonly SheetAction[];
}) {
  const [open, setOpen] = useState(false);
  if (actions.length === 0) return null;
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Actions for ${name}`}
        onClick={() => {
          setOpen(true);
        }}
      >
        <MoreHorizontalIcon aria-hidden="true" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="[overflow-wrap:anywhere]">
              {name}
            </DialogTitle>
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </DialogHeader>
          <ul className="-mx-2 flex flex-col">
            {actions.map((action) => {
              const Icon = action.icon;
              return (
                <li key={action.label}>
                  <button
                    type="button"
                    disabled={action.disabled}
                    onClick={() => {
                      setOpen(false);
                      action.onSelect();
                    }}
                    className={cn(
                      'hover:bg-accent focus-visible:ring-ring flex min-h-11 w-full items-center gap-3 rounded-md px-2 text-left text-base focus-visible:ring-3 focus-visible:outline-none disabled:opacity-60',
                      action.destructive && 'text-destructive',
                    )}
                  >
                    {Icon ? (
                      <Icon aria-hidden={true} className="size-5 shrink-0" />
                    ) : null}
                    {action.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
